import { IconButton, Menu, MenuItem, Stack, Typography } from "@mui/material";
import EmailIcon from "src/assets/svg-icons/email.svg";
import CloseIcon from "src/assets/svg-icons/close_trans.svg";
import WhatsAppIcon from "src/assets/svg-icons/whatsapp.svg";
import CalcIcon from "src/assets/svg-icons/logo.svg";
import { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import DownloadIcon from "src/assets/svg-icons/download.svg";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ConfirmPopup } from "src/shared/components/confirm-popup";
import { specificBranchHasPermission } from "src/utils";
import { PurchasingPermissions } from "src/constants/permissions";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  useCancelPoMutation,
  useSendPoMutation,
} from "src/api/inventory/reorder";

interface IAction {
  row: any;
  refetch: Function;
}

const TableActions = (props: IAction) => {
  const { row, refetch } = props;
  const { id, reference, is_canceled } = row;
  const { t } = useTranslation("inventory");
  const { t: generalT } = useTranslation("general");
  const navigate = useNavigate();
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openAttachmentsModal, setOpenAttachmentsModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [actionType, setActionType] = useState<string>();

  // APIS
  const { mutate, isPending, status, error } = useCancelPoMutation();
  const {
    mutate: mutateSend,
    error: errorSend,
    status: statusSend,
    isPending: isPendingSend,
  } = useSendPoMutation();

  //permissions
  const editPermission = specificBranchHasPermission(
    PurchasingPermissions.editPurchasing,
    row.branch_id
  );
  const deletePermission = specificBranchHasPermission(
    PurchasingPermissions.deletePurchasing,
    row.branch_id
  );
  const payPermission = specificBranchHasPermission(
    PurchasingPermissions.payPurchasing,
    row.branch_id
  );

  const handleOpenEdit = () => setOpenEdit(true);
  const handleCloseEdit = () => setOpenEdit(false);

  const handleOpenConfirm = (type: string) => {
    setActionType(type);
    setOpenConfirm(true);
  };

  const handleCloseConfirm = () => setOpenConfirm(false);

  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const navigateToPoDetails = () => {
    navigate(`/inventory/reorder/po/${id}`);
  };

  const handleConfirm = () => {
    if (actionType === "cancel") {
      mutate({ id });
    } else if (actionType === "sendApp") {
      mutateSend({ id, send_method: "app" });
    } else if (actionType === "sendEmail") {
      mutateSend({ id, send_method: "email" });
    }
  };

  useEffect(() => {
    if (status === "success") {
      refetch();
      handleCloseConfirm();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: t("cancelled"),
        },
      ]);
    } else if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data.errors[0].value || t("somethingWrong"),
        },
      ]);
    }
  }, [status]);

  useEffect(() => {
    if (statusSend === "success") {
      refetch();
      handleCloseConfirm();
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: generalT("done"),
        },
      ]);
    } else if (statusSend === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: errorSend?.data.errors[0].value || t("somethingWrong"),
        },
      ]);
    }
  }, [statusSend]);

  return (
    <Stack direction={"row"} spacing={"12px"} justifyContent={"center"}>
      <IconButton size="small" onClick={(e) => handleClick(e)}>
        <MoreVertIcon />
      </IconButton>

      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        <MenuItem
          onClick={(e) => handleOpenConfirm("sendEmail")}
          sx={{ gap: 1 }}
        >
          <img width={17} src={EmailIcon} alt="" />
          <Typography
            color={"#1D2939"}
            fontSize={"16px"}
            textTransform={"capitalize"}
          >
            {t("sendEmail")}
          </Typography>
        </MenuItem>
        {true && (
          <MenuItem
            onClick={(e) => handleOpenConfirm("sendApp")}
            sx={{ gap: 1 }}
          >
            <img width={16} src={CalcIcon} alt="" />
            <Typography
              color={"#1D2939"}
              fontSize={"16px"}
              textTransform={"capitalize"}
            >
              {t("sendApp")}
            </Typography>
          </MenuItem>
        )}

        {!is_canceled && (
          <MenuItem
            sx={{ gap: 1 }}
            onClick={(e) => handleOpenConfirm("cancel")}
          >
            <img width={17} src={CloseIcon} alt="" />
            <Typography
              color={"#1D2939"}
              fontSize={"16px"}
              textTransform={"capitalize"}
            >
              {t("cancel")}
            </Typography>
          </MenuItem>
        )}

        <MenuItem sx={{ gap: 1 }}>
          <img width={15} src={WhatsAppIcon} alt="" />
          <Typography
            color={"#1D2939"}
            fontSize={"16px"}
            textTransform={"capitalize"}
          >
            {t("shareWhatsApp")}
          </Typography>
        </MenuItem>

        <MenuItem onClick={navigateToPoDetails} sx={{ gap: 1 }}>
          <VisibilityOutlinedIcon sx={{ fontSize: "16px" }} />
          <Typography
            color={"#1D2939"}
            fontSize={"16px"}
            textTransform={"capitalize"}
          >
            {t("details")}
          </Typography>
        </MenuItem>

        <MenuItem onClick={(e) => {}} sx={{ gap: 1 }}>
          <img src={DownloadIcon} alt="" />
          <Typography
            color={"#1D2939"}
            fontSize={"16px"}
            textTransform={"capitalize"}
          >
            {generalT("download")}
          </Typography>
        </MenuItem>
      </Menu>

      {/* confirm delete popup */}
      <ConfirmPopup
        open={openConfirm}
        item={""}
        handleClose={handleCloseConfirm}
        handleConfirm={handleConfirm}
        isPending={isPending || isPendingSend}
        title={
          actionType === "sendApp"
            ? t("sendApp")
            : actionType === "sendEmail"
            ? t("sendEmail")
            : actionType === "cancel"
            ? generalT("cancel")
            : t("cancel")
        }
        subtitle={generalT("areYouSureToConfirm")}
        confirmBtnText={generalT("confirm")}
        noIcon
      />
    </Stack>
  );
};
export default TableActions;
